import React, { useState, useEffect, useMemo } from 'react';
import { doc, setDoc, deleteDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useExerciseLibrary } from '../hooks/useExerciseLibrary';
import { useTranslation } from '../context/LanguageContext';
import { MediaLightboxModal } from './MediaLightboxModal';

/* ==========================================
   HELPER: TITLE CASE FORMATTER
   ========================================== */
const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  return str
    .trim()
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (!word) return '';
      if (word.includes('-')) {
        return word.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const normalizeExerciseName = (str) => {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/^\d+[\.\)]\s*/, '')
    .replace(/[^a-z0-9ąćęłńóśźż\s]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const getDefaultGhostWeight = (bodyPart, name) => {
  const cleanBp = (bodyPart || '').toLowerCase().trim();
  const cleanName = (name || '').toLowerCase().trim();

  if (cleanBp.includes('core') || cleanBp.includes('brzuch') || cleanName.includes('robak') || cleanName.includes('dead bug') || cleanName.includes('plank')) return '10';
  if (cleanBp.includes('leg') || cleanBp.includes('nogi')) return '40';
  if (cleanBp.includes('chest') || cleanBp.includes('klatka')) return '30';
  if (cleanBp.includes('back') || cleanBp.includes('plecy')) return '30';
  if (cleanBp.includes('shoulder') || cleanBp.includes('barki')) return '10';
  if (cleanBp.includes('bicep') || cleanBp.includes('tricep') || cleanBp.includes('arm')) return '10';
  return '15';
};

/* ==========================================
   INTELLIGENT OVERLOAD & EQUIPMENT ROUNDING
   ========================================== */
const calculateOverloadWeight = (baseWeight, overloadPct, equipment, bodyPart, setType) => {
  const rawWeight = parseFloat(baseWeight || 0);
  if (!rawWeight || isNaN(rawWeight) || rawWeight <= 0) return baseWeight || "0";

  // WARM-UP SET EXEMPTION: Warm-up sets ('Warm-up') NEVER get overload applied!
  if (setType === 'Warm-up') {
    return String(rawWeight);
  }

  if (!overloadPct || overloadPct === 0) {
    return String(rawWeight);
  }

  const rawIncremented = rawWeight * (1 + (overloadPct / 100));
  const eq = (equipment || '').toLowerCase();

  let rounded = rawIncremented;

  // Dumbbells / Kettlebells: round to nearest 1 kg or 2 kg
  if (eq.includes('dumbbell') || eq.includes('hantle') || eq.includes('kettlebell')) {
    if (rawIncremented < 10) {
      rounded = Math.round(rawIncremented);
    } else {
      rounded = Math.round(rawIncremented / 2) * 2;
    }
  } 
  // Barbells / Cables / Machines / Default:
  else {
    if (rawIncremented > 25) {
      rounded = Math.round(rawIncremented / 2.5) * 2.5;
    } else {
      rounded = Math.round(rawIncremented / 1.25) * 1.25;
    }
  }

  const cleanStr = String(Math.round(rounded * 100) / 100);
  return cleanStr;
};

export default function UserDiary({ userId, trainerId, userProfile, selectedRoutine, lastCompletedWorkout, initialWorkoutDraft }) {
  const { t, lang } = useTranslation();
  const { exercises: catalog } = useExerciseLibrary(trainerId);

  // Active Stopwatch State (seconds elapsed)
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatStopwatch = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Catalog & Filters State
  const [selectedCatalogEx, setSelectedCatalogEx] = useState('');
  const [selectedBodyPartFilter, setSelectedBodyPartFilter] = useState('ALL');
  const [selectedSourceFilter, setSelectedSourceFilter] = useState('ALL');
  const [selectedEquipmentFilter, setSelectedEquipmentFilter] = useState('ALL');
  const [exerciseSearchQuery, setExerciseSearchQuery] = useState('');
  const [enlargedUserImage, setEnlargedUserImage] = useState(null);

  // Completion Modal State
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [saveAsReusableRoutine, setSaveAsReusableRoutine] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [isSaveSuccess, setIsSaveSuccess] = useState(false);
  const [draggedExerciseIdx, setDraggedExerciseIdx] = useState(null);
  const [dragOverExerciseIdx, setDragOverExerciseIdx] = useState(null);

  // Title Editing State
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [customWorkoutTitle, setCustomWorkoutTitle] = useState(
    initialWorkoutDraft?.routineTitle || selectedRoutine?.title || (lang === 'pl' ? "Własny Trening" : "Custom Workout")
  );

  const [ghostOverloadPct, setGhostOverloadPct] = useState(0);
  const [previousSessionState, setPreviousSessionState] = useState({});

  // Fetch Ghost Exercise History
  useEffect(() => {
    if (!userId) return;

    const fetchGhostHistory = async () => {
      try {
        const q = query(
          collection(db, "workouts"),
          where("userId", "==", userId)
        );
        
        const querySnapshot = await getDocs(q).catch(() => null);
        if (!querySnapshot || querySnapshot.empty) return;

        const docs = querySnapshot.docs.map(d => d.data()).sort((a, b) => {
          const timeA = typeof a.createdAt === 'number' ? a.createdAt : (a.createdAt?.toMillis?.() || new Date(a.date || 0).getTime());
          const timeB = typeof b.createdAt === 'number' ? b.createdAt : (b.createdAt?.toMillis?.() || new Date(b.date || 0).getTime());
          return timeB - timeA;
        });

        const ghostMap = {};
        for (const log of docs) {
          for (const ex of (log.exercises || [])) {
            if (!ex.sets || ex.sets.length === 0) continue;

            const validSets = ex.sets.filter(s => (s.weight !== undefined && s.weight !== "") || (s.reps !== undefined && s.reps !== ""));
            const setsToStore = validSets.length > 0 ? validSets : ex.sets;

            if (ex.exerciseId && !ghostMap[ex.exerciseId]) {
              ghostMap[ex.exerciseId] = setsToStore;
            }
            if (ex.name && !ghostMap[ex.name]) {
              ghostMap[ex.name] = setsToStore;
            }
            const norm = normalizeExerciseName(ex.name);
            if (norm && !ghostMap[norm]) {
              ghostMap[norm] = setsToStore;
            }
          }
        }

        setPreviousSessionState(ghostMap);

        setWorkout(prev => ({
          ...prev,
          exercises: prev.exercises.map(ex => {
            const norm = normalizeExerciseName(ex.name);
            const pastSets = (ex.exerciseId && ghostMap[ex.exerciseId]) || (ex.name && ghostMap[ex.name]) || (norm && ghostMap[norm]);
            if (!pastSets || pastSets.length === 0) return ex;

            return {
              ...ex,
              sets: ex.sets.map((s, idx) => {
                const past = pastSets[idx] || pastSets[pastSets.length - 1] || pastSets[0];
                const pastW = past?.weight || past?.ghostWeight;
                const pastR = past?.reps || past?.ghostReps;

                return {
                  ...s,
                  weight: s.weight ? String(s.weight) : "",
                  reps: s.reps ? String(s.reps) : "",
                  ghostWeight: (pastW !== undefined && pastW !== "") ? String(pastW) : (s.ghostWeight || getDefaultGhostWeight(ex.bodyPart, ex.name)),
                  ghostReps: (pastR !== undefined && pastR !== "") ? String(pastR) : (s.ghostReps || "12")
                };
              })
            };
          })
        }));
      } catch (error) {
        console.error("Error fetching ghost exercise history:", error);
      }
    };

    fetchGhostHistory();
  }, [userId, selectedRoutine]);

  const buildDefaultWorkoutFromRoutine = (routine, uId, tId, lastWk, l) => {
    const workoutUuid = crypto.randomUUID(); 
    const targetMuscles = Array.from(new Set((routine.exercises || []).map(ex => ex.bodyPart || ex.targetMuscle)));
    const exerciseIds = (routine.exercises || []).map(ex => ex.id || ex.exerciseId);

    return {
      id: workoutUuid,
      userId: uId,
      trainerId: tId || null, 
      routineId: routine.id,
      routineVersion: routine.version || 1,
      planId: routine.planId || null,
      planTitle: routine.planTitle || null,
      routineTitle: routine.title || "Custom Workout",
      date: new Date().toISOString().split('T')[0], 
      rpe: 8,
      caloriesBurned: "",
      targetMuscles: targetMuscles, 
      exerciseIds: exerciseIds,     
      exercises: (routine.exercises || []).map(ex => {
        const norm = normalizeExerciseName(ex.name);
        const pastEx = lastWk?.exercises?.find(p => p.exerciseId === (ex.id || ex.exerciseId) || p.name === ex.name || normalizeExerciseName(p.name) === norm);
        
        const totalSets = (Array.isArray(ex.sets) ? ex.sets.length : parseInt(ex.sets)) || parseInt(ex.setsCount) || 3;
        const defaultReps = (typeof ex.reps === 'object' ? (Array.isArray(ex.reps) ? (ex.reps[0]?.reps || "12") : "12") : (ex.reps ?? "12")).toString();
        
        return {
          exerciseId: ex.id || ex.exerciseId || ("ex_" + Math.random()), 
          name: ex.name,       
          bodyPart: ex.bodyPart || ex.targetMuscle || 'General',
          equipment: ex.equipment || '',
          hints: ex.hints || ex.notes || ex.remarks || "",
          notes: ex.notes || ex.hints || ex.remarks || "",
          remarks: ex.remarks ? (l === 'pl' ? `Skopiowano: ${ex.remarks}` : `Copied remark: ${ex.remarks}`) : "",
          sets: Array.from({ length: totalSets }, (_, index) => {
            const currentSetNumber = index + 1;
            const existingSet = Array.isArray(ex.sets) ? ex.sets[index] : null;
            const pastSet = ex.historicalSets ? ex.historicalSets[index] : (pastEx?.sets?.find(s => s.setNumber === currentSetNumber) || pastEx?.sets?.[index] || pastEx?.sets?.[pastEx.sets.length - 1]);
            
            const pastReps = pastSet?.reps || pastSet?.ghostReps || defaultReps;
            const pastWeight = pastSet?.weight || pastSet?.ghostWeight || "";

            const targetGhostW = existingSet?.ghostWeight || existingSet?.weight || pastWeight || getDefaultGhostWeight(ex.bodyPart || ex.targetMuscle, ex.name);
            const targetGhostR = existingSet?.ghostReps || existingSet?.reps || pastReps || defaultReps;

            return {
              setNumber: currentSetNumber, 
              type: existingSet?.type || 'Normal',
              weight: "",
              reps: "",
              completed: false,
              ghostWeight: String(targetGhostW || ""),
              ghostReps: String(targetGhostR || "")
            };
          })
        };
      })
    };
  };

  const draftKey = `gymghost_workout_draft_${userId || 'guest'}_${selectedRoutine.id || 'default'}`;
  const [expandedHints, setExpandedHints] = useState({});

  const toggleHint = (exIndex) => {
    setExpandedHints(prev => ({
      ...prev,
      [exIndex]: prev[exIndex] === undefined ? false : !prev[exIndex]
    }));
  };

  const [workout, setWorkout] = useState(() => {
    if (selectedRoutine && selectedRoutine.workout) {
      return selectedRoutine.workout;
    }
    if (initialWorkoutDraft) {
      return initialWorkoutDraft;
    }
    return buildDefaultWorkoutFromRoutine(selectedRoutine, userId, trainerId, lastCompletedWorkout, lang);
  });

  // Sync draft / localStorage
  useEffect(() => {
    if (workout && workout.exercises) {
      try {
        localStorage.setItem(draftKey, JSON.stringify(workout));
        const draftDocId = `${userId || 'guest'}_${selectedRoutine.id || 'custom'}`;
        const draftPayload = {
          id: draftDocId,
          key: draftKey,
          userId: userId || 'guest',
          userEmail: userProfile?.email || '',
          routineId: selectedRoutine.id,
          routineTitle: workout.routineTitle || selectedRoutine.title || 'Trening Roboczy',
          selectedRoutine: selectedRoutine,
          workout: workout,
          updatedAt: Date.now(),
          date: workout.date || new Date().toISOString().split('T')[0]
        };
        localStorage.setItem(`gymghost_draft_meta_${draftDocId}`, JSON.stringify(draftPayload));
        if (userId && selectedRoutine?.id) {
          setDoc(doc(db, "workout_drafts", draftDocId), draftPayload, { merge: true }).catch(() => {});
        }
      } catch (e) {}
    }
  }, [workout, draftKey, userId, selectedRoutine]);

  // Live Telemetry Stats (Volume & Completed Sets)
  const liveStats = useMemo(() => {
    let totalVolume = 0;
    let completedSetsCount = 0;
    let totalSetsCount = 0;

    (workout?.exercises || []).forEach(ex => {
      (ex.sets || []).forEach(s => {
        totalSetsCount += 1;
        if (s.completed) {
          completedSetsCount += 1;
          const w = parseFloat(s.weight || 0);
          const r = parseInt(s.reps || 0);
          if (!isNaN(w) && !isNaN(r)) {
            totalVolume += (w * r);
          }
        }
      });
    });

    return { totalVolume, completedSetsCount, totalSetsCount };
  }, [workout]);

  // GHOST FEATURE HANDLERS WITH WARM-UP EXEMPTION & EQUIPMENT ROUNDING (1-TAP COMPLETION)
  const handleCopySet = (exIndex, setIndex) => {
    const targetEx = workout.exercises[exIndex];
    const targetSet = targetEx.sets[setIndex];
    if (targetSet.ghostWeight || targetSet.ghostReps) {
      const calculatedW = calculateOverloadWeight(
        targetSet.ghostWeight, 
        ghostOverloadPct, 
        targetEx.equipment, 
        targetEx.bodyPart, 
        targetSet.type
      );

      setWorkout(prev => ({
        ...prev,
        exercises: prev.exercises.map((ex, idx) => {
          if (idx !== exIndex) return ex;
          return {
            ...ex,
            sets: ex.sets.map((s, sIdx) => {
              if (sIdx !== setIndex) return s;
              return {
                ...s,
                weight: String(calculatedW),
                reps: String(targetSet.ghostReps || '10'),
                completed: true // 1-TAP COMPLETION!
              };
            })
          };
        })
      }));
    }
  };

  const handleCopyGhostForExercise = (exerciseIndex) => {
    const ex = workout.exercises[exerciseIndex];
    if (!ex || !ex.sets) return;

    const updatedSets = ex.sets.map(s => {
      const targetW = calculateOverloadWeight(
        s.ghostWeight || s.weight, 
        ghostOverloadPct, 
        ex.equipment, 
        ex.bodyPart, 
        s.type
      );

      return {
        ...s,
        weight: String(targetW || s.ghostWeight || s.weight || "10"),
        reps: String(s.ghostReps || s.reps || "10")
      };
    });

    setWorkout(prev => {
      const nextExercises = [...prev.exercises];
      nextExercises[exerciseIndex] = { ...nextExercises[exerciseIndex], sets: updatedSets };
      return { ...prev, exercises: nextExercises };
    });
  };

  const handleGhostOverloadChange = (newPct) => {
    setGhostOverloadPct(newPct);

    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.map(ex => ({
        ...ex,
        sets: ex.sets.map(s => {
          const baseW = s.ghostWeight || s.weight;
          const targetW = calculateOverloadWeight(
            baseW, 
            newPct, 
            ex.equipment, 
            ex.bodyPart, 
            s.type
          );
          return {
            ...s,
            ghostWeight: String(targetW || baseW || "")
          };
        })
      }))
    }));
  };

  // "COPY PREVIOUS SET & MARK DONE" (Row N <- Row N-1)
  const handleCopyPreviousSetAndMarkDone = (exIndex, setIndex) => {
    if (setIndex === 0) return;
    const prevSet = workout.exercises[exIndex].sets[setIndex - 1];
    const targetW = prevSet.weight || prevSet.ghostWeight || '10';
    const targetR = prevSet.reps || prevSet.ghostReps || '10';

    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.map((ex, idx) => {
        if (idx !== exIndex) return ex;
        return {
          ...ex,
          sets: ex.sets.map((s, sIdx) => {
            if (sIdx !== setIndex) return s;
            return { 
              ...s, 
              weight: String(targetW), 
              reps: String(targetR),
              completed: true // AUTOMATICALLY MARK AS DONE!
            };
          })
        };
      })
    }));
  };

  const handleCycleSetType = (exIndex, setIndex) => {
    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.map((ex, idx) => {
        if (idx !== exIndex) return ex;
        return {
          ...ex,
          sets: ex.sets.map((set, sIdx) => {
            if (sIdx !== setIndex) return set;
            const currentType = set.type || 'Normal';
            let nextType = 'Normal';
            if (currentType === 'Normal') nextType = 'Warm-up';
            else if (currentType === 'Warm-up') nextType = 'Drop-set';
            else nextType = 'Normal';

            return { ...set, type: nextType };
          })
        };
      })
    }));
  };

  // Set Row Handlers
  const handleSetChange = (exIndex, setIndex, field, value) => {
    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.map((ex, idx) => {
        if (idx !== exIndex) return ex;
        return {
          ...ex,
          sets: ex.sets.map((set, sIdx) => {
            if (sIdx !== setIndex) return set;
            return { ...set, [field]: value };
          })
        };
      })
    }));
  };

  const handleToggleCompleteSet = (exIndex, setIndex) => {
    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.map((ex, idx) => {
        if (idx !== exIndex) return ex;
        return {
          ...ex,
          sets: ex.sets.map((set, sIdx) => {
            if (sIdx !== setIndex) return set;
            const nextCompleted = !set.completed;
            
            let nextWeight = set.weight;
            let nextReps = set.reps;
            if (nextCompleted && (!nextWeight || nextWeight === '')) {
              nextWeight = set.ghostWeight || '10';
            }
            if (nextCompleted && (!nextReps || nextReps === '')) {
              nextReps = set.ghostReps || '10';
            }

            return { 
              ...set, 
              completed: nextCompleted, 
              weight: nextWeight, 
              reps: nextReps 
            };
          })
        };
      })
    }));
  };

  const handleStepValue = (exIndex, setIndex, field, step) => {
    const currentSet = workout.exercises[exIndex].sets[setIndex];
    const currentVal = parseFloat(currentSet[field] || currentSet[field === 'weight' ? 'ghostWeight' : 'ghostReps'] || 0);
    const newVal = Math.max(0, Math.round((currentVal + step) * 10) / 10);
    handleSetChange(exIndex, setIndex, field, String(newVal));
  };

  const handleWeightStep = (exIndex, setIndex, isIncrement) => {
    const currentSet = workout.exercises[exIndex].sets[setIndex];
    const currentVal = parseFloat(currentSet.weight || currentSet.ghostWeight || 0);
    
    // Dynamic step sizing: 0.5 kg for weight <= 40 kg, 1 kg for weight > 40 kg
    let stepAmount = 0.5;
    if (isIncrement) {
      stepAmount = currentVal >= 40 ? 1 : 0.5;
    } else {
      stepAmount = currentVal > 40 ? 1 : 0.5;
    }

    const delta = isIncrement ? stepAmount : -stepAmount;
    const newVal = Math.max(0, Math.round((currentVal + delta) * 10) / 10);
    handleSetChange(exIndex, setIndex, 'weight', String(newVal));
  };

  const handleAddSet = (exIndex) => {
    setWorkout(prev => {
      const targetEx = prev.exercises[exIndex];
      const nextSetNum = targetEx.sets.length + 1;
      const lastSet = targetEx.sets[targetEx.sets.length - 1];
      const newSet = {
        setNumber: nextSetNum,
        type: 'Normal',
        weight: lastSet ? lastSet.weight : "",
        reps: lastSet ? lastSet.reps : "",
        completed: false,
        ghostWeight: lastSet ? lastSet.ghostWeight : "10",
        ghostReps: lastSet ? lastSet.ghostReps : "10"
      };
      const updatedExercises = [...prev.exercises];
      updatedExercises[exIndex] = { ...targetEx, sets: [...targetEx.sets, newSet] };
      return { ...prev, exercises: updatedExercises };
    });
  };

  const handleRemoveSet = (exIndex, setIndex) => {
    setWorkout(prev => {
      const targetEx = prev.exercises[exIndex];
      if (targetEx.sets.length <= 1) return prev;
      const updatedSets = targetEx.sets
        .filter((_, sIdx) => sIdx !== setIndex)
        .map((set, sIdx) => ({ ...set, setNumber: sIdx + 1 }));
      const updatedExercises = [...prev.exercises];
      updatedExercises[exIndex] = { ...targetEx, sets: updatedSets };
      return { ...prev, exercises: updatedExercises };
    });
  };

  const handleRemoveExercise = (exIndex) => {
    setWorkout(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, idx) => idx !== exIndex)
    }));
  };

    // Add exercise object directly from 1000+ catalog card
  const handleAddExerciseObjectToSession = (catalogItem) => {
    if (!catalogItem) return;
    
    const pastEx = lastCompletedWorkout?.exercises?.find(p => p.name === catalogItem.name);
    const defaultWeight = catalogItem.bodyPart === 'Legs' ? '40' : '20';

    const primaryHint = lang === 'pl'
      ? (catalogItem.hintsPL || catalogItem.hints_pl || catalogItem.hints || catalogItem.notes || catalogItem.remarks || "")
      : (catalogItem.hintsEN || catalogItem.hints_en || catalogItem.hints || catalogItem.notes || catalogItem.remarks || "");

    const newEx = {
      exerciseId: "session_ex_" + Date.now() + "_" + Math.random().toString(36).substr(2, 4),
      name: catalogItem.name,
      nameEN: catalogItem.nameEN || catalogItem.name,
      namePL: catalogItem.namePL || catalogItem.name,
      bodyPart: catalogItem.bodyPart || 'General',
      equipment: catalogItem.equipment || '',
      imageUrl: catalogItem.imageUrl || "",
      imageUrl2: catalogItem.imageUrl2 || "",
      images: catalogItem.images || [],
      subPart: catalogItem.subPart || "",
      hints: primaryHint,
      notes: primaryHint,
      remarks: "",
      sets: [1, 2, 3].map(num => ({
        setNumber: num,
        type: 'Normal',
        weight: "",
        reps: "",
        completed: false,
        ghostWeight: pastEx?.sets?.[num - 1]?.weight || defaultWeight,
        ghostReps: pastEx?.sets?.[num - 1]?.reps || "10"
      }))
    };

    setWorkout(prev => ({
      ...prev,
      exercises: [...prev.exercises, newEx],
      targetMuscles: Array.from(new Set([...prev.targetMuscles, newEx.bodyPart]))
    }));

    setTimeout(() => {
      const inputs = document.querySelectorAll('input[placeholder="kg"], input[placeholder="reps"], input[placeholder="8–10"], input[placeholder="10"]');
      if (inputs.length > 0) {
        const lastInput = inputs[inputs.length - 1];
        lastInput.focus();
        lastInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 120);
  };

    // Reorder exercises helper function
  const handleMoveExercise = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || toIdx < 0 || toIdx >= workout.exercises.length) return;
    setWorkout(prev => {
      const updated = [...prev.exercises];
      const [movedItem] = updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, movedItem);
      return { ...prev, exercises: updated };
    });
  };

  // Debounced auto-save effect (800ms)
  useEffect(() => {
    if (!workout || !workout.exercises || workout.exercises.length === 0) return;
    const timer = setTimeout(() => {
      try {
        const routineId = selectedRoutine?.id || workout.id || "custom_session";
        const draftKey = `gym_ghost_draft_${userId}_${routineId}`;
        const payload = {
          ...workout,
          elapsedSeconds,
          savedAt: Date.now()
        };
        localStorage.setItem(draftKey, JSON.stringify(payload));
      } catch (e) {}
    }, 800);
    return () => clearTimeout(timer);
  }, [workout.exercises, workout.rpe, workout.caloriesBurned, workout.notes, userId, selectedRoutine, elapsedSeconds]);

  const handleAddExerciseToSession = () => {
    if (!selectedCatalogEx) return;
    const catalogItem = catalog.find(c => String(c.id) === String(selectedCatalogEx) || String(c.docId) === String(selectedCatalogEx));
    if (!catalogItem) return;
    
    const pastEx = lastCompletedWorkout?.exercises?.find(p => p.name === catalogItem.name);
    const defaultWeight = catalogItem.bodyPart === 'Legs' ? '40' : '20';

    const primaryHint = lang === 'pl'
      ? (catalogItem.hintsPL || catalogItem.hints_pl || catalogItem.hints || catalogItem.notes || catalogItem.remarks || "")
      : (catalogItem.hintsEN || catalogItem.hints_en || catalogItem.hints || catalogItem.notes || catalogItem.remarks || "");

    const newEx = {
      exerciseId: "session_ex_" + Date.now(),
      name: catalogItem.name,
      nameEN: catalogItem.nameEN || catalogItem.name,
      namePL: catalogItem.namePL || catalogItem.name,
      bodyPart: catalogItem.bodyPart || 'General',
      equipment: catalogItem.equipment || '',
      imageUrl: catalogItem.imageUrl || "",
      imageUrl2: catalogItem.imageUrl2 || "",
      images: catalogItem.images || [],
      subPart: catalogItem.subPart || "",
      hints: primaryHint,
      notes: primaryHint,
      remarks: "",
      sets: [1, 2, 3].map(num => ({
        setNumber: num,
        type: 'Normal',
        weight: "",
        reps: "",
        completed: false,
        ghostWeight: pastEx?.sets?.[num - 1]?.weight || defaultWeight,
        ghostReps: pastEx?.sets?.[num - 1]?.reps || "10"
      }))
    };

    setWorkout(prev => ({
      ...prev,
      exercises: [...prev.exercises, newEx],
      targetMuscles: Array.from(new Set([...prev.targetMuscles, newEx.bodyPart]))
    }));
    setSelectedCatalogEx('');
  };

  // Final Save Handler
  const handleFinalSaveWorkout = async () => {
    if (workout.exercises.length === 0) {
      alert(lang === 'pl' ? "Proszę dodać przynajmniej jedno ćwiczenie." : "Please add at least one exercise.");
      return;
    }

    const payload = {
      ...workout,
      date: new Date().toISOString().split('T')[0],
      rpe: workout.rpe || 8,
      caloriesBurned: workout.caloriesBurned === '' ? 0 : (Number(workout.caloriesBurned) || 0),
      durationSeconds: elapsedSeconds,
      totalVolumeLifted: liveStats.totalVolume,
      completedSetsCount: liveStats.completedSetsCount,
      routineTitle: workout.routineTitle || selectedRoutine?.title || "Custom Workout",
      createdAt: Date.now()
    };

    try {
      await setDoc(doc(db, "workouts", workout.id), { ...payload, createdAt: serverTimestamp() });
      
      if (saveAsReusableRoutine) {
        try {
          await handleSaveAsPersonalRoutine();
        } catch (routineErr) {
          console.error("Error saving routine template:", routineErr);
        }
      }
      
      try {
        const routineId = selectedRoutine.id;
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && (key.includes(routineId) || (workout.id && key.includes(workout.id)))) {
            localStorage.removeItem(key);
          }
        }
        if (userId && selectedRoutine?.id) {
          deleteDoc(doc(db, "workout_drafts", `${userId}_${selectedRoutine.id}`)).catch(() => {});
        }
      } catch (e) {}

      window.dispatchEvent(new CustomEvent('local_workout_saved', { detail: payload }));
      setShowCompletionModal(false);
      alert(lang === 'pl' ? `🎉 Gratulacje! Trening zapisany w dzienniku (${liveStats.totalVolume} kg łącznie)!` : `🎉 Workout complete and saved (${liveStats.totalVolume} kg volume)!`);
    } catch (err) {
      console.error("Save workout error:", err);
      alert("Error saving workout: " + err.message);
    }
  };

  const [saveStatusFeedback, setSaveStatusFeedback] = useState(null);

  const handleSaveDraftProgress = async () => {
    if (!workout) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(workout));
      const draftDocId = `${userId || 'guest'}_${selectedRoutine.id || 'custom'}`;
      const draftPayload = {
        id: draftDocId,
        key: draftKey,
        userId: userId || 'guest',
        userEmail: userProfile?.email || '',
        routineId: selectedRoutine.id,
        routineTitle: workout.routineTitle || selectedRoutine.title || 'Trening Roboczy',
        selectedRoutine: selectedRoutine,
        workout: workout,
        updatedAt: Date.now(),
        date: workout.date || new Date().toISOString().split('T')[0]
      };
      localStorage.setItem(`gymghost_draft_meta_${draftDocId}`, JSON.stringify(draftPayload));
      if (userId && selectedRoutine?.id) {
        await setDoc(doc(db, "workout_drafts", draftDocId), draftPayload, { merge: true });
      }
      setSaveStatusFeedback(lang === 'pl' ? '✓ Postęp zapisany w szkicach!' : '✓ Progress saved to drafts!');
      setTimeout(() => setSaveStatusFeedback(null), 3000);
    } catch (err) {
      console.error("Error saving draft progress:", err);
      alert("Error saving draft: " + err.message);
    }
  };

  const handleSaveAsPersonalRoutine = async () => {
    if (!workout || !userId) return;
    try {
      const routineDocId = `routine_personal_${Date.now()}`;
      const routinePayload = {
        id: routineDocId,
        userId: userId,
        title: workout.routineTitle || selectedRoutine?.title || 'Mój Szablon',
        isGlobal: false,
        exercises: workout.exercises.map(ex => ({
          exerciseId: ex.exerciseId,
          name: ex.name,
          bodyPart: ex.bodyPart,
          equipment: ex.equipment,
          hints: ex.hints || '',
          setsCount: ex.sets ? ex.sets.length : 3,
          reps: ex.sets?.[0]?.reps || "10"
        })),
        createdAt: serverTimestamp()
      };
      await setDoc(doc(db, "routines", routineDocId), routinePayload);
      setSaveStatusFeedback(lang === 'pl' ? '⭐ Zapisano jako Twój nowy Szablon!' : '⭐ Saved as your personal routine!');
      setTimeout(() => setSaveStatusFeedback(null), 3000);
    } catch (err) {
      console.error("Error saving routine template:", err);
      alert("Error saving template: " + err.message);
    }
  };

  return (
    <div className="bg-[#0B0F19] text-slate-100 min-h-screen pb-24 font-sans space-y-4 relative">
      
      {/* FLOATING SAVE CONFIRMATION TOAST NOTIFICATION */}
      {saveStatusFeedback && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-purple-900 text-purple-100 border border-purple-400 font-extrabold text-xs px-4 py-2.5 rounded-2xl shadow-2xl animate-bounce flex items-center gap-2">
          <span className="material-symbols-outlined text-base text-emerald-400">check_circle</span>
          <span>{saveStatusFeedback}</span>
        </div>
      )}
      
      {/* 1. STICKY LIVE WORKOUT STATUS BAR (MOBILE-FIRST) */}
      <header className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 p-3 lg:px-6 shadow-xl flex items-center justify-between gap-3">
        
        {/* Stopwatch & Live Telemetry */}
        <div className="flex items-center gap-3">


          <div className="hidden sm:flex items-center gap-2 text-xs font-extrabold text-slate-300">
            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-lg">
              {lang === 'pl' ? 'Objętość:' : 'Volume:'} <strong className="text-emerald-400">{liveStats.totalVolume} kg</strong>
            </span>
            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1 rounded-lg">
              {lang === 'pl' ? 'Serie:' : 'Sets:'} <strong className="text-indigo-400">{liveStats.completedSetsCount}/{liveStats.totalSetsCount}</strong>
            </span>
          </div>
        </div>

        {/* Header Live Telemetry (No duplicate CTAs) */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-xl shadow-sm">
            <span className="material-symbols-outlined text-sm">verified</span>
            <span>{lang === 'pl' ? 'Dziennik Treningowy na Żywo' : 'Live Workout Session'}</span>
          </span>
        </div>
      </header>

      {/* WORKOUT TITLE & GHOST OVERLOAD COCKPIT */}
      <div className="px-4 lg:px-6 pt-2 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  autoFocus
                  value={workout.routineTitle || customWorkoutTitle}
                  onChange={(e) => {
                    setCustomWorkoutTitle(e.target.value);
                    setWorkout(prev => ({ ...prev, routineTitle: e.target.value }));
                  }}
                  className="bg-slate-950 border-2 border-indigo-500 text-white font-extrabold text-base rounded-xl px-3 h-10 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setIsEditingTitle(false)}
                  className="bg-indigo-600 text-white font-bold text-xs px-3 h-10 rounded-xl cursor-pointer"
                >
                  <span className="material-symbols-outlined text-xs">check</span>
                </button>
              </div>
            ) : (
              <h2
                onClick={() => setIsEditingTitle(true)}
                className="text-lg lg:text-xl font-extrabold text-white tracking-tight m-0 cursor-pointer hover:text-indigo-300 transition flex items-center gap-2 truncate"
              >
                <span className="truncate">{toTitleCase(workout.routineTitle || customWorkoutTitle || selectedRoutine?.title)}</span>
                <span className="material-symbols-outlined text-sm text-slate-500 shrink-0">edit</span>
              </h2>
            )}
          </div>

          <span className="text-xs text-slate-400 font-semibold bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg shrink-0">
            {workout.date}
          </span>
        </div>

        {/* PROMINENT GHOST OVERLOAD COCKPIT BANNER WITH MATERIAL SYMBOLS */}
        <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl p-3.5 shadow-xl flex items-center justify-between flex-wrap gap-3 relative z-20">
          <div className="flex items-center gap-2.5">
            <span className="material-symbols-outlined text-emerald-400 text-xl shrink-0">trending_up</span>
            <div>
              <span className="text-xs font-black text-emerald-300 block uppercase tracking-wider">
                {lang === 'pl' ? 'GymGhost Progresja (Ghost Overload)' : 'GymGhost Overload System'}
              </span>
              <span className="text-[11px] text-slate-400 font-medium">
                {lang === 'pl' ? 'Inteligentna progresja ciężaru z wyłączeniem serii rozgrzewkowych (W)' : 'Smart progressive overload math with warm-up set exemption'}
              </span>
            </div>
          </div>

          {/* Ghost Overload Percentage Selector */}
          <div className="flex items-center gap-2 relative z-30 shrink-0">
            <span className="text-xs font-extrabold text-slate-400 hidden sm:flex items-center gap-1">
              <span className="material-symbols-outlined text-sm text-emerald-400">trending_up</span>
              <span>{lang === 'pl' ? 'Overload:' : 'Overload:'}</span>
            </span>
            <div className="relative">
              <select
                value={ghostOverloadPct}
                onChange={(e) => handleGhostOverloadChange(Number(e.target.value))}
                className="bg-slate-950 border border-emerald-500/40 text-emerald-300 font-extrabold text-xs rounded-xl pl-3.5 pr-8 h-10 min-w-[140px] sm:min-w-[155px] outline-none cursor-pointer shadow-inner appearance-none relative z-30 leading-snug py-1.5"
              >
                <option value="0">{lang === 'pl' ? '0% Overload' : '0% (Exact Ghost)'}</option>
                <option value="1">+1% Overload</option>
                <option value="2">+2% Overload</option>
                <option value="2.5">+2.5% Overload</option>
                <option value="5">+5% Overload</option>
                <option value="10">+10% Overload</option>
              </select>
              <span className="material-symbols-outlined absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-emerald-400 pointer-events-none z-30">expand_more</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2 & 3. EXERCISE CARDS & HIGH-SPEED INTERACTIVE SET TABLES WITH GHOST FEATURES */}
      <main className="px-4 lg:px-6 space-y-4">
        {workout.exercises.length === 0 ? (
          <div className="p-8 text-center bg-slate-900/60 border-2 border-dashed border-slate-800 rounded-2xl space-y-3">
            <span className="material-symbols-outlined text-3xl text-indigo-400">fitness_center</span>
            <h4 className="text-sm font-extrabold text-white m-0">
              {lang === 'pl' ? "Brak ćwiczeń w sesji" : "No exercises in workout session"}
            </h4>
            <p className="text-xs text-slate-400 max-w-sm mx-auto m-0">
              {lang === 'pl' ? "Dodaj ćwiczenia z poniższego katalogu, aby rozpocząć rejestrację serii." : "Add exercises from the catalog below to start logging sets."}
            </p>
            <button
              type="button"
              onClick={() => {
                const addSection = document.getElementById('add-movement-section');
                if (addSection) addSection.scrollIntoView({ behavior: 'smooth' });
                const searchInput = document.getElementById('on-the-fly-exercise-search');
                if (searchInput) searchInput.focus();
              }}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs px-4.5 py-2.5 rounded-xl shadow-lg border border-emerald-500/40 transition cursor-pointer active:scale-95 mt-2"
            >
              <span className="material-symbols-outlined text-base">add_circle</span>
              <span>{lang === 'pl' ? '+ Dodaj Pierwsze Ćwiczenie' : '+ Add First Exercise'}</span>
            </button>
          </div>
        ) : (
          workout.exercises.map((ex, exIdx) => {
            const mediaUrl = ex.imageUrl || ex.gif_url || ex.image || "";
            const isCoachHintOpen = expandedHints[exIdx] !== false;
            const hintText = ex.hints || ex.notes || ex.remarks || "";

            const isDragged = draggedExerciseIdx === exIdx;
            const isDragOver = dragOverExerciseIdx === exIdx;

            return (
              <div key={ex.exerciseId || exIdx} className="space-y-2">
                {/* Drop Insertion Line */}
                {isDragOver && (
                  <div className="h-1.5 bg-emerald-400 rounded-full my-2 animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.8)] transition-all" />
                )}

                <div
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', String(exIdx));
                    setDraggedExerciseIdx(exIdx);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOverExerciseIdx !== exIdx) setDragOverExerciseIdx(exIdx);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromIdx = Number(e.dataTransfer.getData('text/plain'));
                    if (!isNaN(fromIdx) && fromIdx !== exIdx) {
                      handleMoveExercise(fromIdx, exIdx);
                    }
                    setDraggedExerciseIdx(null);
                    setDragOverExerciseIdx(null);
                  }}
                  onDragEnd={() => {
                    setDraggedExerciseIdx(null);
                    setDragOverExerciseIdx(null);
                  }}
                  className={`bg-slate-900 border rounded-2xl p-4 lg:p-5 space-y-3.5 shadow-xl transition-all duration-150 ${
                    isDragged 
                      ? 'opacity-60 scale-[1.01] border-emerald-500/60 ring-2 ring-emerald-500/30 shadow-2xl' 
                      : 'border-slate-800 hover:border-slate-700/80'
                  }`}
                >
                {/* CARD HEADER */}
                <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-800/80 pb-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    
                    {/* Exercise Title & Badges */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Drag Handle Icon */}
                        <div 
                          className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-200 p-1 rounded-lg touch-none flex items-center justify-center shrink-0 select-none"
                          title={lang === 'pl' ? "Przeciągnij, aby zmienić kolejność" : "Drag to reorder exercise"}
                          aria-roledescription="sortable"
                        >
                          <span className="material-symbols-outlined text-lg select-none">drag_indicator</span>
                        </div>

                        {/* Move Up & Move Down Buttons (Mobile Ergonomics) */}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            disabled={exIdx === 0}
                            onClick={() => handleMoveExercise(exIdx, exIdx - 1)}
                            className="w-7 h-7 rounded-lg bg-slate-950 border border-slate-800 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition cursor-pointer shrink-0 active:scale-90"
                            title={lang === 'pl' ? "Przesuń ćwiczenie w górę" : "Move exercise up"}
                          >
                            <span className="material-symbols-outlined text-xs">north</span>
                          </button>
                          <button
                            type="button"
                            disabled={exIdx === workout.exercises.length - 1}
                            onClick={() => handleMoveExercise(exIdx, exIdx + 1)}
                            className="w-7 h-7 rounded-lg bg-slate-950 border border-slate-800 text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition cursor-pointer shrink-0 active:scale-90"
                            title={lang === 'pl' ? "Przesuń ćwiczenie w dół" : "Move exercise down"}
                          >
                            <span className="material-symbols-outlined text-xs">south</span>
                          </button>
                        </div>

                        <span className="w-6 h-6 rounded-lg bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 text-xs font-bold flex items-center justify-center shrink-0">
                          {exIdx + 1}
                        </span>
                        <h3 className="font-extrabold text-sm lg:text-base text-white m-0 tracking-tight truncate">
                          {toTitleCase(lang === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name))}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                          {ex.bodyPart || 'General'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Header Quick Actions (Including Copy Ghost Button) */}
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    
                    {/* COPY ALL GHOST TARGETS FOR THIS EXERCISE BUTTON */}
                    <button
                      type="button"
                      onClick={() => handleCopyGhostForExercise(exIdx)}
                      className="bg-slate-950 hover:bg-slate-850 text-emerald-400 border border-emerald-500/40 hover:border-emerald-400 text-xs font-extrabold px-3 h-9 rounded-xl transition cursor-pointer flex items-center gap-1.5 shadow-sm active:scale-95"
                      title={lang === 'pl' ? "Wypełnij wszystkie serie docelowym ciężarem Ghosta" : "Copy ghost target weights across all sets"}
                    >
                      <span className="material-symbols-outlined text-sm text-emerald-400">auto_graph</span>
                      <span>{lang === 'pl' ? 'Kopiuj Ghost' : 'Copy Ghost'} {ghostOverloadPct > 0 ? `(+${ghostOverloadPct}%)` : ''}</span>
                    </button>

                    {/* Media Video Lightbox Icon */}
                    {mediaUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          const imgList = Array.isArray(ex.images) && ex.images.length > 0 ? ex.images : [mediaUrl, ex.gif_url, ex.image, ex.imageUrl2].filter(Boolean);
                          setEnlargedUserImage({
                            url: mediaUrl,
                            imageUrl2: ex.imageUrl2,
                            images: Array.from(new Set(imgList)),
                            title: toTitleCase(ex.namePL || ex.name)
                          });
                        }}
                        className="w-9 h-9 rounded-xl bg-slate-950 border border-slate-800 text-indigo-400 hover:text-white flex items-center justify-center transition cursor-pointer"
                        title={lang === 'pl' ? "Podgląd wideo / zdjęcia" : "View exercise media"}
                      >
                        <span className="material-symbols-outlined text-sm">visibility</span>
                      </button>
                    )}

                    {/* Coach Hint Toggle */}
                    {hintText && (
                      <button
                        type="button"
                        onClick={() => toggleHint(exIdx)}
                        className={`w-9 h-9 rounded-xl border text-xs font-black transition cursor-pointer flex items-center justify-center ${
                          isCoachHintOpen 
                            ? 'bg-indigo-600 text-white border-indigo-400' 
                            : 'bg-slate-950 text-slate-400 hover:text-slate-200 border-slate-800'
                        }`}
                        title={lang === 'pl' ? "Wskazówki trenera" : "Coach cues"}
                      >
                        <span className="material-symbols-outlined text-sm">info</span>
                      </button>
                    )}

                    {/* Delete Exercise Button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveExercise(exIdx)}
                      className="w-9 h-9 rounded-xl bg-slate-950 border border-slate-800 text-slate-500 hover:text-rose-400 hover:border-rose-500/40 flex items-center justify-center transition cursor-pointer"
                      title={lang === 'pl' ? "Usuń ćwiczenie" : "Delete exercise"}
                    >
                      <span className="material-symbols-outlined text-sm">delete_outline</span>
                    </button>
                  </div>
                </div>

                {/* COLLAPSIBLE COACH GUIDANCE BANNER */}
                {hintText && isCoachHintOpen && (
                  <div className="bg-indigo-950/40 border border-indigo-500/30 p-3 rounded-xl flex items-start gap-2.5 text-xs text-indigo-200">
                    <span className="material-symbols-outlined text-sm text-indigo-400 shrink-0 mt-0.5">info</span>
                    <p className="m-0 leading-relaxed italic text-slate-300">
                      "{hintText}"
                    </p>
                  </div>
                )}

                {/* HIGH-SPEED INTERACTIVE SET TABLE WITH GHOST TARGET BUTTONS & SLEEK COPY PREVIOUS */}
                <div className="bg-slate-950/80 border border-slate-800/80 rounded-xl p-2.5 space-y-2 shadow-inner">
                  
                  {/* Table Headers */}
                  <div className="grid grid-cols-12 gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 pb-1 border-b border-slate-800/80 text-center">
                    <div className="col-span-2 text-left">{lang === 'pl' ? 'SERIA' : 'SET'}</div>
                    <div className="col-span-3">{lang === 'pl' ? 'GHOST TARGET' : 'GHOST TARGET'}</div>
                    <div className="col-span-3">{lang === 'pl' ? 'KG' : 'WEIGHT'}</div>
                    <div className="col-span-2">{lang === 'pl' ? 'POWT.' : 'REPS'}</div>
                    <div className="col-span-2">{lang === 'pl' ? 'ZALICZONE' : 'DONE'}</div>
                  </div>

                  {/* Set Rows */}
                  {ex.sets.map((s, sIdx) => {
                    const isCompleted = !!s.completed;
                    const hasGhost = !!(s.ghostWeight || s.ghostReps);
                    const prevSet = sIdx > 0 ? ex.sets[sIdx - 1] : null;
                    const canCopyPrevious = sIdx > 0 && !isCompleted && (prevSet?.completed || prevSet?.weight || prevSet?.reps);

                    // Compute dynamic display weight for Ghost Target button with Warm-up exemption & Equipment math
                    const computedGhostW = calculateOverloadWeight(
                      s.ghostWeight || s.weight, 
                      ghostOverloadPct, 
                      ex.equipment, 
                      ex.bodyPart, 
                      s.type
                    );

                    return (
                      <div
                        key={sIdx}
                        className={`grid grid-cols-12 gap-1.5 items-center p-1.5 rounded-xl transition-all ${
                          isCompleted 
                            ? 'bg-emerald-950/20 border border-emerald-500/30 opacity-75' 
                            : 'bg-slate-900/60 border border-slate-800/60 opacity-100'
                        }`}
                      >
                        {/* Col 1: Set Number & Type Toggle Button + Contextual Copy & Done Arrow */}
                        <div className="col-span-2 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleCycleSetType(exIdx, sIdx)}
                            className={`w-7 h-7 rounded-lg text-xs font-black flex items-center justify-center shrink-0 border cursor-pointer transition active:scale-90 shadow-sm ${
                              s.type === 'Warm-up'
                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30'
                                : s.type === 'Drop-set'
                                ? 'bg-rose-500/20 text-rose-300 border-rose-500/40 hover:bg-rose-500/30'
                                : isCompleted
                                ? 'bg-emerald-600 text-white border-emerald-400'
                                : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-600'
                            }`}
                            title={lang === 'pl' ? `Typ: ${s.type || 'Normalna'} (Kliknij, aby zmienić: Normalna -> Rozgrzewkowa W -> Drop-set D)` : `Type: ${s.type || 'Normal'} (Tap to toggle: Normal -> Warm-up W -> Drop-set D)`}
                          >
                            {s.type === 'Warm-up' ? 'W' : (s.type === 'Drop-set' ? 'D' : `#${s.setNumber}`)}
                          </button>

                          {/* SLEEK COPY PREVIOUS SET & MARK DONE BUTTON (Only if previous set is active/filled and current set not completed) */}
                          {canCopyPrevious && (
                            <button
                              type="button"
                              onClick={() => handleCopyPreviousSetAndMarkDone(exIdx, sIdx)}
                              className="text-slate-400 hover:text-indigo-300 border border-slate-800 hover:border-slate-700 bg-slate-950/60 text-[10px] font-bold px-1 py-0.5 rounded flex items-center gap-0.5 transition cursor-pointer active:scale-95 shrink-0"
                              title={lang === 'pl' ? "Skopiuj wynik z poprzedniej serii i zalicz" : "Copy previous set & mark done"}
                            >
                              <span className="material-symbols-outlined text-[11px]">south</span>
                              <span className="text-[9px] font-extrabold">{lang === 'pl' ? 'Tak samo' : 'Same'}</span>
                            </button>
                          )}
                        </div>

                        {/* Col 2: Ghost Target Interactive Button */}
                        <div className="col-span-3 flex justify-center">
                          {hasGhost ? (
                            <button
                              type="button"
                              onClick={() => handleCopySet(exIdx, sIdx)}
                              className={`w-full h-9 border rounded-lg text-[11px] font-extrabold transition cursor-pointer flex items-center justify-center gap-1.5 shadow-inner active:scale-95 px-1 truncate ${
                                s.type === 'Warm-up'
                                  ? 'bg-amber-950/40 text-amber-300 border-amber-500/30 hover:border-amber-400'
                                  : 'bg-emerald-950/80 hover:bg-emerald-900/90 text-emerald-300 border-emerald-500/40 hover:border-emerald-400'
                              }`}
                              title={lang === 'pl' 
                                ? `Kliknij, aby zaliczyć z celem Ghosta: ${computedGhostW}kg × ${s.ghostReps}${s.type === 'Warm-up' ? ' (Bez overload)' : ''}` 
                                : `Tap to apply Ghost target & mark done: ${computedGhostW}kg × ${s.ghostReps}${s.type === 'Warm-up' ? ' (No overload)' : ''}`}
                            >
                              <span className="material-symbols-outlined text-xs text-emerald-400 shrink-0">auto_graph</span>
                              <span className="truncate">{computedGhostW || '0'}kg×{s.ghostReps || '10'}</span>
                            </button>
                          ) : (
                            <span className="text-slate-600 text-xs font-bold">—</span>
                          )}
                        </div>

                        {/* Col 3: Weight Input with +/- Steppers (0.5 kg step <= 40 kg, 1 kg step > 40 kg) */}
                        <div className="col-span-3 flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleWeightStep(exIdx, sIdx, false)}
                            className="w-6 h-9 rounded-lg bg-slate-950 text-slate-400 hover:text-white border border-slate-800 font-extrabold text-xs flex items-center justify-center shrink-0 active:scale-90 cursor-pointer"
                            title={lang === 'pl' ? "Odejmij ciężar" : "Decrease weight"}
                          >
                            <span className="material-symbols-outlined text-xs">remove</span>
                          </button>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={s.weight}
                            onChange={(e) => handleSetChange(exIdx, sIdx, 'weight', e.target.value)}
                            placeholder={computedGhostW || "0"}
                            className={`w-full h-9 bg-slate-950 border text-center font-extrabold text-xs rounded-lg outline-none shadow-inner ${
                              isCompleted ? 'border-emerald-500/60 text-emerald-300' : 'border-slate-800 text-white focus:border-indigo-500'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => handleWeightStep(exIdx, sIdx, true)}
                            className="w-6 h-9 rounded-lg bg-slate-950 text-slate-400 hover:text-white border border-slate-800 font-extrabold text-xs flex items-center justify-center shrink-0 active:scale-90 cursor-pointer"
                            title={lang === 'pl' ? "Dodaj ciężar" : "Increase weight"}
                          >
                            <span className="material-symbols-outlined text-xs">add</span>
                          </button>
                        </div>

                        {/* Col 4: Reps Input */}
                        <div className="col-span-2 flex items-center gap-1">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={s.reps}
                            onChange={(e) => handleSetChange(exIdx, sIdx, 'reps', e.target.value)}
                            placeholder={s.ghostReps || "10"}
                            className={`w-full h-9 bg-slate-950 border text-center font-extrabold text-xs rounded-lg outline-none shadow-inner ${
                              isCompleted ? 'border-emerald-500/60 text-emerald-300' : 'border-slate-800 text-white focus:border-indigo-500'
                            }`}
                          />
                        </div>

                        {/* Col 5: ✓ Complete Checkbox Button & Delete Set Button */}
                        <div className="col-span-2 flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleToggleCompleteSet(exIdx, sIdx)}
                            className={`w-9 h-9 rounded-xl font-black transition-all cursor-pointer flex items-center justify-center min-w-[36px] min-h-[36px] ${
                              isCompleted 
                                ? 'bg-emerald-600 text-white border-2 border-emerald-400 shadow-md shadow-emerald-950/50 scale-105' 
                                : 'bg-slate-950 text-slate-500 border border-slate-800 hover:border-slate-700'
                            }`}
                            title={lang === 'pl' ? "Oznacz jako zaliczone" : "Toggle complete"}
                          >
                            <span className="material-symbols-outlined text-base">
                              {isCompleted ? 'check_circle' : 'radio_button_unchecked'}
                            </span>
                          </button>

                          {ex.sets.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSet(exIdx, sIdx)}
                              className="w-8 h-8 rounded-xl bg-slate-950 hover:bg-rose-950/60 text-slate-500 hover:text-rose-400 border border-slate-800 hover:border-rose-500/40 flex items-center justify-center transition cursor-pointer shrink-0 active:scale-95"
                              title={lang === 'pl' ? "Usuń tę serię" : "Remove this set"}
                            >
                              <span className="material-symbols-outlined text-xs">delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add & Remove Set Row Buttons */}
                  <div className="pt-1 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleAddSet(exIdx)}
                      className="text-xs font-bold text-indigo-400 hover:text-white bg-indigo-500/10 hover:bg-indigo-600 px-3 py-1.5 rounded-lg transition cursor-pointer flex items-center gap-1 border border-indigo-500/20 active:scale-95"
                    >
                      <span className="material-symbols-outlined text-xs">add</span>
                      <span>{lang === 'pl' ? 'Dodaj Serię' : 'Add Set'}</span>
                    </button>

                    {ex.sets.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSet(exIdx, ex.sets.length - 1)}
                        className="text-xs font-bold text-rose-400 hover:text-white bg-rose-500/10 hover:bg-rose-600 px-3 py-1.5 rounded-lg transition cursor-pointer flex items-center gap-1 border border-rose-500/20 active:scale-95"
                      >
                        <span className="material-symbols-outlined text-xs">remove</span>
                        <span>{lang === 'pl' ? 'Usuń Serię' : 'Remove Set'}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Personal Remark Input */}
                <input
                  type="text"
                  value={ex.remarks || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setWorkout(prev => ({
                      ...prev,
                      exercises: prev.exercises.map((item, idx) => idx === exIdx ? { ...item, remarks: val } : item)
                    }));
                  }}
                  placeholder={lang === 'pl' ? "Dodaj notatkę (np. Dobra pompka, lekki zapas)..." : "Add personal note for this movement..."}
                  className="w-full bg-slate-950 border border-slate-800 text-slate-200 text-xs rounded-xl p-2.5 outline-none shadow-inner"
                />
              </div>
            </div>
            );
          })
        )}
      </main>

      {/* ADD EXERCISE TO SESSION SECTION — HIGH-SPEED INTERACTIVE CATALOG PANEL */}
      <section id="add-movement-section" className="px-4 lg:px-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 lg:p-6 space-y-4 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
            <div>
              <h4 className="text-sm sm:text-base uppercase tracking-wider font-black text-white m-0 flex items-center gap-2">
                <span className="material-symbols-outlined text-lg text-emerald-400">add_circle</span>
                <span>{lang === 'pl' ? 'Katalog Ćwiczeń w Locie (1,000+ Ćwiczeń)' : 'Add Movement On-the-Fly (1,000+ Library)'}</span>
              </h4>
              <p className="text-xs text-slate-400 m-0 mt-1 font-medium">
                {lang === 'pl' 
                  ? 'Wyszukuj po nazwie, partii ciała lub sprzęcie i dodawaj ćwiczenia 1 kliknięciem.'
                  : 'Search by movement name, muscle group, or equipment, and add exercises in 1 click.'}
              </p>
            </div>

            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-xl shadow-sm self-start sm:self-auto">
              <span className="material-symbols-outlined text-sm">fitness_center</span>
              <span>{catalog.length} {lang === 'pl' ? 'Ćwiczeń w Bazie' : 'Available Exercises'}</span>
            </span>
          </div>

          {/* Instant Search Bar & Dropdown Combobox Filters */}
          <div className="space-y-3 bg-slate-950 p-4 rounded-xl border border-slate-800/80 shadow-inner">
            {/* Search Input */}
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-base pointer-events-none select-none">
                search
              </span>
              <input
                id="on-the-fly-exercise-search"
                type="text"
                value={exerciseSearchQuery}
                onChange={(e) => setExerciseSearchQuery(e.target.value)}
                placeholder={lang === 'pl' ? "Szukaj ćwiczenia po nazwie lub partii..." : "Search exercise by name or muscle..."}
                className="w-full bg-slate-900 border border-slate-800 focus:border-emerald-500 text-slate-100 font-bold text-xs sm:text-sm rounded-xl pl-10 pr-10 py-3 outline-none transition shadow-inner placeholder:text-slate-500"
              />
              {exerciseSearchQuery && (
                <button
                  type="button"
                  onClick={() => setExerciseSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition cursor-pointer text-sm font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Muscle Group & Equipment Dropdown Comboboxes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Muscle Group Combobox */}
              <div className="space-y-1">
                <label className="block text-[11px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  <span className="material-symbols-outlined text-xs text-emerald-400">fitness_center</span>
                  <span>{lang === 'pl' ? 'Partia Ciała:' : 'Muscle Group:'}</span>
                </label>
                <select
                  value={selectedBodyPartFilter}
                  onChange={(e) => setSelectedBodyPartFilter(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 focus:border-emerald-500 text-slate-200 font-extrabold text-xs rounded-xl px-3 py-2.5 outline-none cursor-pointer shadow-inner"
                >
                  <option value="ALL">{lang === 'pl' ? "Wszystkie partie ciała" : "All body parts"}</option>
                  <option value="chest">{lang === 'pl' ? "Klatka piersiowa (Chest)" : "Chest"}</option>
                  <option value="back">{lang === 'pl' ? "Plecy i grzbiet (Back)" : "Back"}</option>
                  <option value="shoulders">{lang === 'pl' ? "Barki (Shoulders)" : "Shoulders"}</option>
                  <option value="upper arms">{lang === 'pl' ? "Ramiona (Upper arms)" : "Upper arms"}</option>
                  <option value="forearms">{lang === 'pl' ? "Przedramiona (Forearms)" : "Forearms"}</option>
                  <option value="upper legs">{lang === 'pl' ? "Uda i pośladki (Legs)" : "Upper legs"}</option>
                  <option value="lower legs">{lang === 'pl' ? "Łydki (Calves)" : "Lower legs"}</option>
                  <option value="waist">{lang === 'pl' ? "Brzuch / Core" : "Waist / Core"}</option>
                  <option value="cardio">{lang === 'pl' ? "Kardio" : "Cardio"}</option>
                </select>
              </div>

              {/* Equipment Combobox */}
              <div className="space-y-1">
                <label className="block text-[11px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  <span className="material-symbols-outlined text-xs text-teal-400">build</span>
                  <span>{lang === 'pl' ? 'Sprzęt / Przyrząd:' : 'Equipment:'}</span>
                </label>
                <select
                  value={selectedEquipmentFilter}
                  onChange={(e) => setSelectedEquipmentFilter(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 focus:border-emerald-500 text-slate-200 font-extrabold text-xs rounded-xl px-3 py-2.5 outline-none cursor-pointer shadow-inner"
                >
                  <option value="ALL">{lang === 'pl' ? "Wszystek sprzęt" : "All equipment"}</option>
                  <option value="barbell">{lang === 'pl' ? "Sztanga" : "Barbell"}</option>
                  <option value="dumbbell">{lang === 'pl' ? "Hantle" : "Dumbbells"}</option>
                  <option value="cable">{lang === 'pl' ? "Wyciąg" : "Cable"}</option>
                  <option value="leverage machine">{lang === 'pl' ? "Maszyny" : "Machines"}</option>
                  <option value="body weight">{lang === 'pl' ? "Ciężar ciała" : "Body weight"}</option>
                  <option value="band">{lang === 'pl' ? "Guma oporowa" : "Bands"}</option>
                  <option value="kettlebell">Kettlebell</option>
                  <option value="smith machine">Suwnica Smitha</option>
                </select>
              </div>
            </div>
          </div>

          {/* Interactive Exercise Cards Grid with 1-Click Add Buttons */}
          {(() => {
            const filteredCatalog = catalog.filter(c => {
              if (selectedBodyPartFilter !== 'ALL') {
                const bodyStr = (c.bodyPart || c.category || c.targetMuscle || '').toLowerCase();
                if (!bodyStr.includes(selectedBodyPartFilter.toLowerCase())) return false;
              }
              if (selectedEquipmentFilter !== 'ALL') {
                const eqStr = (c.equipment || '').toLowerCase();
                if (!eqStr.includes(selectedEquipmentFilter.toLowerCase())) return false;
              }
              if (exerciseSearchQuery.trim() !== '') {
                const q = exerciseSearchQuery.toLowerCase();
                const nameStr = (c.name || '').toLowerCase();
                const namePLStr = (c.namePL || '').toLowerCase();
                const nameENStr = (c.nameEN || '').toLowerCase();
                const bodyStr = (c.bodyPart || '').toLowerCase();
                if (!nameStr.includes(q) && !namePLStr.includes(q) && !nameENStr.includes(q) && !bodyStr.includes(q)) return false;
              }
              return true;
            });

            if (filteredCatalog.length === 0) {
              return (
                <div className="p-6 text-center bg-slate-950/60 rounded-xl border border-dashed border-slate-800 text-slate-400 font-medium text-xs">
                  {lang === 'pl' ? "Brak ćwiczeń spełniających wybrane kryteria wyszukiwania." : "No exercises matching selected filters."}
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[360px] overflow-y-auto pr-1">
                {filteredCatalog.slice(0, 24).map((c, idx) => {
                  const mediaUrl = c.imageUrl || c.gif_url || c.image || "";
                  const exNameDisplay = toTitleCase(lang === 'pl' ? (c.namePL || c.name) : (c.nameEN || c.name));

                  return (
                    <div key={c.id || idx} className="bg-slate-950 p-3 rounded-xl border border-slate-800/90 hover:border-emerald-500/40 transition flex items-center justify-between gap-3 shadow-inner group">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        {mediaUrl ? (
                          <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-800 shrink-0 bg-slate-900">
                            <img src={mediaUrl} alt={exNameDisplay} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 text-sm font-black">
                            {exNameDisplay.charAt(0)}
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          <h5 className="text-xs font-black text-slate-100 m-0 truncate group-hover:text-emerald-300 transition">
                            {exNameDisplay}
                          </h5>
                          <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-400 font-extrabold uppercase">
                            <span>{c.bodyPart || 'General'}</span>
                            {c.equipment && <span>• {c.equipment}</span>}
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleAddExerciseObjectToSession(c)}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs px-3 py-2 rounded-xl transition cursor-pointer shrink-0 shadow-md border border-emerald-500/30 flex items-center gap-1 active:scale-95"
                      >
                        <span className="material-symbols-outlined text-xs">add</span>
                        <span>{lang === 'pl' ? 'Dodaj' : 'Add'}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* SESSION SUMMARY & FEEDBACK FOOTER CARD */}
      <section className="px-4 lg:px-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 lg:p-5 space-y-4 shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-indigo-400 text-base">tune</span>
              <h4 className="text-xs uppercase tracking-wider font-extrabold text-slate-200 m-0">
                {lang === 'pl' ? 'Podsumowanie Sesji i Uwagi (RPE & Kalorie)' : 'Session Summary & Feedback'}
              </h4>
            </div>
            <span className="text-[10px] font-extrabold text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-0.5 rounded-full">
              {lang === 'pl' ? 'Opcjonalne' : 'Optional'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Session RPE Selector */}
            <div className="space-y-1.5">
              <label className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                {lang === 'pl' ? 'Ocena Intensywności RPE (1-10):' : 'Session RPE (Intensity):'}
              </label>
              <select
                value={workout.rpe || 8}
                onChange={(e) => setWorkout(prev => ({ ...prev, rpe: parseInt(e.target.value) }))}
                className="w-full bg-slate-950 border border-slate-800 hover:border-sky-500/50 text-sky-300 font-extrabold text-xs rounded-xl px-3 h-11 outline-none transition cursor-pointer shadow-inner"
              >
                <option value={10}>10/10 — {lang === 'pl' ? 'Maksymalny wysiłek (Do odmowy)' : 'All-out Failure'}</option>
                <option value={9}>9/10 — {lang === 'pl' ? 'Bardzo ciężko (1 powtórzenie zapasu)' : 'Very Tough (1 RIR)'}</option>
                <option value={8}>8/10 — {lang === 'pl' ? 'Solidny wysiłek (2 powtórzenia zapasu)' : 'Solid Effort (2 RIR)'}</option>
                <option value={7}>7/10 — {lang === 'pl' ? 'Umiarkowany wysiłek' : 'Moderate Effort'}</option>
                <option value={6}>6/10 — {lang === 'pl' ? 'Lekki / Rozgrzewkowy' : 'Light / Warm-up'}</option>
              </select>
            </div>

            {/* Calories Burned */}
            <div className="space-y-1.5">
              <label className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                {lang === 'pl' ? 'Spalone Kalorie:' : 'Calories Burned:'}
              </label>
              <div className="relative flex items-center">
                <input
                  type="number"
                  value={workout.caloriesBurned ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0);
                    setWorkout(prev => ({ ...prev, caloriesBurned: val }));
                  }}
                  placeholder={lang === 'pl' ? 'np. 350' : 'e.g. 350'}
                  className="w-full bg-slate-950 border border-slate-800 hover:border-amber-500/50 text-amber-300 font-extrabold text-xs rounded-xl pl-3 pr-12 h-11 outline-none shadow-inner"
                />
                <span className="absolute right-3 text-xs font-extrabold text-slate-500 pointer-events-none">
                  kcal
                </span>
              </div>
            </div>
          </div>

          {/* Session Notes Textarea */}
          <div className="space-y-1.5 pt-1">
            <label className="block text-xs font-extrabold text-slate-400 uppercase tracking-wider">
              {lang === 'pl' ? 'Uwagi i Odczucia z Treningu:' : 'Session Notes & Feedback:'}
            </label>
            <textarea
              rows={3}
              value={workout.notes || ''}
              onChange={(e) => setWorkout(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={lang === 'pl' ? "np. Świetna pompa w klatce, lekki dyskomfort w prawym barku..." : "e.g., Felt strong on bench press, good energy levels..."}
              className="w-full bg-slate-950 border border-slate-800 text-slate-100 font-medium text-xs rounded-xl p-3 outline-none focus:border-indigo-500 shadow-inner resize-none min-h-[96px] placeholder:text-slate-500"
            />
          </div>


        </div>
      </section>

      

{/* FINISH WORKOUT COMPLETION MODAL */}
      {showCompletionModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setShowCompletionModal(false)}
        >
          <div 
            className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-lg w-full space-y-5 shadow-2xl relative"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-2xl text-emerald-400">emoji_events</span>
                <div>
                  <h3 className="text-base font-extrabold text-white m-0">
                    {lang === 'pl' ? 'Trening Ukończony!' : 'Workout Complete!'}
                  </h3>
                  <span className="text-xs text-slate-400 font-medium">{workout.routineTitle}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCompletionModal(false)}
                className="text-slate-400 hover:text-white font-bold text-sm"
              >
                ✕
              </button>
            </div>

            {/* Summary Telemetry Badges */}
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">{lang === 'pl' ? 'OBJĘTOŚĆ' : 'VOLUME'}</span>
                <strong className="text-sm font-extrabold text-indigo-400">{liveStats.totalVolume} kg</strong>
              </div>
              <div className="bg-slate-950 p-3 rounded-2xl border border-slate-800">
                <span className="text-[10px] uppercase font-bold text-slate-400 block">{lang === 'pl' ? 'SERIE' : 'SETS'}</span>
                <strong className="text-sm font-extrabold text-white">{liveStats.completedSetsCount}/{liveStats.totalSetsCount}</strong>
              </div>
            </div>

            {/* Session RPE Rating Chips & Selector */}
            <div className="space-y-2">
              <label className="block text-xs uppercase font-extrabold text-slate-300">{lang === 'pl' ? 'Ocena Intensywności (RPE):' : 'Session RPE:'}</label>
              <div className="flex items-center gap-2">
                {[6, 7, 8, 9, 10].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setWorkout(prev => ({ ...prev, rpe: val }))}
                    className={`flex-1 py-2 rounded-xl text-xs font-black transition border cursor-pointer ${
                      (workout.rpe || 8) === val 
                        ? 'bg-sky-500 text-slate-950 border-sky-400 shadow-md scale-105' 
                        : 'bg-slate-950 text-slate-300 border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    {val}
                  </button>
                ))}
              </div>
              <select
                value={workout.rpe || 8}
                onChange={(e) => setWorkout(prev => ({ ...prev, rpe: parseInt(e.target.value) }))}
                className="w-full bg-slate-950 border border-slate-800 text-sky-300 font-bold text-xs rounded-xl px-3 h-10 outline-none cursor-pointer mt-1"
              >
                <option value={10}>10/10 — {lang === 'pl' ? 'Maksymalny wysiłek (Do odmowy)' : 'All-out Failure'}</option>
                <option value={9}>9/10 — {lang === 'pl' ? 'Bardzo ciężko (1 powt. zapasu)' : 'Very Hard (1 RIR)'}</option>
                <option value={8}>8/10 — {lang === 'pl' ? 'Solidny opór (2 powt. zapasu)' : 'Solid Effort (2 RIR)'}</option>
                <option value={7}>7/10 — {lang === 'pl' ? 'Umiarkowany wysiłek' : 'Moderate'}</option>
                <option value={6}>6/10 — {lang === 'pl' ? 'Regeneracyjny / Lekki' : 'Light / Recovery'}</option>
              </select>
            </div>

            {/* Calories Burned */}
            <div className="space-y-1.5">
              <label className="block text-xs uppercase font-extrabold text-slate-300">{lang === 'pl' ? 'Spalone Kalorie (kcal):' : 'Calories Burned (kcal):'}</label>
              <div className="relative flex items-center">
                <input
                  type="number"
                  value={workout.caloriesBurned ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0);
                    setWorkout(prev => ({ ...prev, caloriesBurned: val }));
                  }}
                  placeholder="np. 350"
                  className="w-full bg-slate-950 border border-slate-800 text-amber-300 font-bold text-xs rounded-xl pl-3 pr-12 h-11 outline-none"
                />
                <span className="absolute right-3 text-xs font-extrabold text-slate-500 pointer-events-none">kcal</span>
              </div>
            </div>

            {/* Post-Workout Notes & Feedback */}
            <div className="space-y-1.5">
              <label className="block text-xs uppercase font-extrabold text-slate-300">{lang === 'pl' ? 'Uwagi do Treningu:' : 'Session Notes & Feedback:'}</label>
              <textarea
                rows={2}
                value={workout.notes || ''}
                onChange={(e) => setWorkout(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={lang === 'pl' ? "Wpisz dodatkowe przemyślenia z treningu..." : "Add workout feedback for your log & coach..."}
                className="w-full bg-slate-950 border border-slate-800 text-slate-100 font-medium text-xs rounded-xl p-2.5 outline-none focus:border-indigo-500 shadow-inner resize-none placeholder:text-slate-500"
              />
            </div>

            {/* Save as Reusable Routine Template Checkbox */}
            <label className="flex items-center gap-2.5 bg-slate-950 p-3 rounded-xl border border-slate-800 cursor-pointer hover:border-indigo-500/50 transition select-none">
              <input
                type="checkbox"
                checked={saveAsReusableRoutine}
                onChange={(e) => setSaveAsReusableRoutine(e.target.checked)}
                className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-slate-900 cursor-pointer"
              />
              <span className="text-xs font-bold text-slate-200">
                {lang === 'pl' ? 'Zapisz ten trening jako własny szablon w moich rutynach' : 'Save this workout as a reusable routine template'}
              </span>
            </label>

            {/* Action Buttons */}
            <div className="pt-2 flex flex-col gap-2">
              <button
                type="button"
                onClick={handleFinalSaveWorkout}
                className="w-full bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-black text-sm py-3.5 rounded-xl shadow-lg shadow-emerald-950/40 cursor-pointer transition uppercase tracking-wider"
              >
                {lang === 'pl' ? 'Zapisz i Ukończ Trening' : 'Save & Complete Session'}
              </button>
              <button
                type="button"
                onClick={() => setShowCompletionModal(false)}
                className="w-full text-slate-400 hover:text-white font-bold text-xs py-2 cursor-pointer"
              >
                {lang === 'pl' ? 'Powrót do edycji' : 'Return to Workout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* INSTANT SAVE DRAFT TOAST NOTIFICATION */}
      {showSaveToast && (
        <div className="fixed bottom-16 right-4 z-50 bg-emerald-600 text-white font-bold text-xs px-4 py-2.5 rounded-xl shadow-2xl border border-emerald-400 flex items-center gap-2 animate-bounce">
          <span className="material-symbols-outlined text-sm">check_circle</span>
          <span>{lang === 'pl' ? 'Szkic został zapisany!' : 'Draft saved successfully!'}</span>
        </div>
      )}

      {/* PERSISTENT STICKY BOTTOM ACTION BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-950/95 backdrop-blur-md border-t border-slate-800 p-3 px-4 lg:px-6 shadow-2xl flex items-center justify-between gap-3">
        {/* Left Section: Live Workout Telemetry (Timer, Completed Sets, Volume Load) */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Live Duration Timer */}
          <div className="flex items-center gap-1.5 bg-slate-950 border border-slate-800 px-3 py-1.5 rounded-xl font-mono text-emerald-400 font-extrabold text-xs shadow-inner">
            <span className="material-symbols-outlined text-xs text-emerald-400">timer</span>
            <span>{formatStopwatch(elapsedSeconds)}</span>
          </div>

          {/* Live Sets & Volume Badges */}
          <div className="text-xs font-extrabold text-slate-300 flex items-center gap-1.5">
            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1.5 rounded-xl text-slate-200">
              {liveStats.completedSetsCount}/{liveStats.totalSetsCount} {lang === 'pl' ? 'Serii' : 'Sets'}
            </span>
            <span className="bg-slate-950 border border-slate-800 px-2.5 py-1.5 rounded-xl text-emerald-400">
              {liveStats.totalVolume} kg
            </span>
          </div>
        </div>

        {/* Right Section: Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveDraftProgress}
            className={`text-xs font-bold px-3.5 h-11 rounded-xl transition cursor-pointer flex items-center gap-1.5 active:scale-95 shadow-md border ${
              isSaveSuccess 
                ? 'bg-emerald-950/80 border-emerald-500/60 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.3)]' 
                : 'bg-slate-950 hover:bg-slate-800 text-purple-300 border-purple-500/40'
            }`}
            title={lang === 'pl' ? "Zapisz szkic postępu" : "Save progress draft"}
          >
            <span className={`material-symbols-outlined text-base ${isSaveSuccess ? 'text-emerald-400' : 'text-purple-400'}`}>
              {isSaveSuccess ? 'check_circle' : 'bookmark'}
            </span>
            <span className="hidden sm:inline">
              {isSaveSuccess 
                ? (lang === 'pl' ? 'Zapisano!' : 'Saved!') 
                : (lang === 'pl' ? 'Zapisz Szkic' : 'Save Draft')}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setShowCompletionModal(true)}
            className="bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white font-black text-xs sm:text-sm px-4 sm:px-5 h-11 rounded-xl shadow-lg shadow-emerald-950/50 transition flex items-center gap-1.5 border border-emerald-500/30 cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">check_circle</span>
            <span>{lang === 'pl' ? 'Zakończ Trening' : 'Finish Workout'}</span>
          </button>
        </div>
      </div>

      {/* MEDIA LIGHTBOX MODAL */}
      <MediaLightboxModal 
        mediaData={enlargedUserImage} 
        onClose={() => setEnlargedUserImage(null)} 
        lang={lang} 
      />
    </div>
  );
}
